// FieldIQ — Log-interaction modal + voice note flow

import { useState, useEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Contact,
  Loader2,
  Send,
  Shield,
  ChevronRight,
  ChevronLeft,
  DoorOpen,
  PhoneCall,
  MessageCircle,
  Trash2,
  Mic,
  Square,
  Play,
  QrCode,
  Share2,
  Check,
  Megaphone,
  ArrowRight,
  ClipboardList,
} from "lucide-react";
import { FIELDIQ_API_URL, authFetch } from "../lib/api";
import { logActivity, saveAudioBlob } from "../lib/storage";
import {
  SLUG_EMOJI,
  VOICE_MAX_SECONDS,
  VOICE_SUBMIT_ERROR,
  VOICE_TOPICS,
  WAVEFORM_HEIGHTS,
  fmtVoiceTime,
  submitVoiceMessage,
  useVoiceRecorder,
} from "../lib/voice";

const INTERACTION_TYPES = [
  { id: "door", label: "Door Knock", icon: DoorOpen, color: "text-green-400" },
  {
    id: "call",
    label: "Phone Call",
    icon: PhoneCall,
    color: "text-purple-400",
  },
  {
    id: "message",
    label: "Message",
    icon: MessageCircle,
    color: "text-amber-400",
  },
  {
    id: "contact",
    label: "Contact Added",
    icon: Contact,
    color: "text-cyan-400",
  },
  {
    id: "survey",
    label: "Survey Share",
    icon: ClipboardList,
    color: "text-indigo-400",
  },
];

// ═══════════════════════════════════════════════════════════════════
// SHARED VOICE RECORDING + SUBMISSION
// Used by LogInteractionVoiceFlow and VoterVoiceFlow
// ═══════════════════════════════════════════════════════════════════

const LogInteractionVoiceFlow = ({
  onDone,
  contactName = "",
  preTopics = [],
  facility,
}) => {
  const [step, setStep] = useState(0); // 0=handoff, 1=pitch, 2=record, 3=tag+id, 4=confirm
  const [selectedTopics, setSelectedTopics] = useState(
    new Set(preTopics.filter(Boolean)),
  );
  const [name, setName] = useState(contactName);
  const [phone, setPhone] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [voiceTopicList, setVoiceTopicList] = useState(VOICE_TOPICS);

  useEffect(() => {
    const fid = facility?.facility_id || "";
    const qs = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/topics${qs}`)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setVoiceTopicList(
            data.map((t) => ({
              icon: SLUG_EMOJI[t.slug] || "📋",
              label: t.label || t.slug,
            })),
          );
        }
      })
      .catch(() => {});
  }, [facility]);

  const {
    elapsed,
    audioBlob,
    start: startRecorder,
    stop: stopRecorder,
    reset: resetRecorder,
  } = useVoiceRecorder(() => setStep(3));

  const startRecording = async () => {
    setSubmitError(null);
    if (await startRecorder()) setStep(2);
  };

  const stopRecording = () => {
    stopRecorder();
    setStep(3);
  };

  const resetRecording = () => {
    resetRecorder();
    setStep(1);
  };

  const toggleTopic = (label) =>
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const fmtTime = fmtVoiceTime;

  const submitVoice = async (isAnon) => {
    setAnonymous(isAnon);
    setSubmitting(true);
    setSubmitError(null);
    const result = await submitVoiceMessage({
      audioBlob,
      facility,
      isAnon,
      name,
      phone,
      selectedTopics,
      elapsed,
    });
    setSubmitting(false);
    if (result.ok) setStep(4);
    else setSubmitError(VOICE_SUBMIT_ERROR);
  };

  const navTheme =
    "bg-gradient-to-br from-[#17253F] via-[#1F2F4D] to-[#17253F]";

  // ── STEP 0: Handoff (canvasser prep) ──
  if (step === 0) {
    return (
      <div className={`fixed inset-0 z-50 ${navTheme} overflow-y-auto p-5`}>
        <div className="max-w-lg mx-auto space-y-4 pt-10">
          <button
            onClick={onDone}
            className="flex items-center gap-1.5 text-[#BDD1F0] text-sm"
          >
            <ChevronLeft size={16} /> Back to Log Interaction
          </button>

          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#F97316]/15 border border-[#F97316]/30">
            <div className="w-1.5 h-1.5 rounded-full bg-[#F97316]" />
            <span className="text-[#F97316] text-[9px] font-bold tracking-[0.14em] uppercase">
              For Canvasser
            </span>
          </div>

          <h2 className="text-white text-xl font-bold leading-tight">
            Offer this naturally — never lead with it.
          </h2>

          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-[9px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase mb-2">
              Try Saying
            </div>
            <p className="text-white text-sm leading-relaxed italic">
              "Before I head out — would you want to share what you just told me
              with leadership directly? It's a 30-second message that goes to
              the chair and your reps."
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2.5">
            <div className="text-[9px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase">
              When You Tap Next
            </div>
            {[
              "Phone faces voter — you hold it",
              "They see what we're recording for",
              "They tap Start Recording themselves",
              "If they decline, no harm done",
            ].map((text) => (
              <div key={text} className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded-full bg-[#F97316]/20 border border-[#F97316]/40 flex items-center justify-center flex-shrink-0">
                  <Check size={8} className="text-[#F97316]" strokeWidth={3} />
                </div>
                <span className="text-white text-xs">{text}</span>
              </div>
            ))}
          </div>

          {selectedTopics.size > 0 && (
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3.5">
              <div className="text-[9px] font-bold text-cyan-300 tracking-[0.14em] uppercase mb-2">
                Topics from your conversation
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[...selectedTopics].map((t) => (
                  <span
                    key={t}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-500/40"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => setStep(1)}
            className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2 shadow-lg"
          >
            Show Voter the Pitch <ArrowRight size={15} />
          </button>
          <button
            onClick={onDone}
            className="w-full py-2.5 text-white/60 text-sm text-center"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── STEP 1: Pitch (voter sees) ──
  if (step === 1) {
    return (
      <div className={`fixed inset-0 z-50 ${navTheme} overflow-y-auto p-5`}>
        <div className="max-w-lg mx-auto space-y-4 pt-10">
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <div className="w-5 h-5 border border-white/40 rounded flex items-center justify-center">
              <svg
                viewBox="0 0 36 36"
                className="w-3 h-3"
                stroke="white"
                fill="none"
                strokeWidth="1.5"
              >
                <polygon points="18,3 31,10.5 31,25.5 18,33 5,25.5 5,10.5" />
                <polygon points="18,11 24.5,14.75 24.5,22.25 18,26 11.5,22.25 11.5,14.75" />
              </svg>
            </div>
            <div className="text-white/70 text-[9px] tracking-[0.16em] font-semibold">
              LD4 GOP · VOTER VOICE
            </div>
          </div>

          <div className="text-[#F97316] text-xs font-bold tracking-[0.2em] uppercase">
            Your Voice. Heard.
          </div>
          <h2 className="text-white text-2xl font-bold leading-tight">
            Tell our leaders what you actually think.
          </h2>
          <p className="text-[#BDD1F0] text-sm leading-relaxed">
            Your 30-second message goes directly to LD4 leadership and your
            elected representatives — in your own voice, in your own words.
          </p>

          <div className="flex justify-center py-4">
            <div className="w-24 h-24 rounded-full bg-white/5 border-2 border-white/15 flex items-center justify-center">
              <Mic size={40} className="text-white" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2.5">
            {[
              "Read by LD4 chairs and elected reps",
              "Aggregated weekly into a leadership digest",
              "Never sold, never used for ads",
              "You can request deletion any time",
            ].map((text) => (
              <div key={text} className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#F97316]/20 border border-[#F97316]/40 flex items-center justify-center flex-shrink-0">
                  <Check size={9} className="text-[#F97316]" strokeWidth={3} />
                </div>
                <span className="text-white text-xs">{text}</span>
              </div>
            ))}
          </div>

          <button
            onClick={startRecording}
            className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2 shadow-lg"
          >
            <Mic size={16} /> Start Recording
          </button>
          <button
            onClick={onDone}
            className="w-full py-2.5 text-white/60 text-sm text-center"
          >
            No thanks
          </button>
        </div>
      </div>
    );
  }

  // ── STEP 2: Recording ──
  if (step === 2) {
    const remaining = VOICE_MAX_SECONDS - elapsed;
    return (
      <div
        className={`fixed inset-0 z-50 bg-gradient-to-br from-[#17253F] via-[#1A3163] to-[#17253F] overflow-y-auto p-5`}
      >
        <div className="max-w-lg mx-auto space-y-4 pt-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] font-bold tracking-[0.16em] text-white/80 uppercase">
                Recording
              </span>
            </div>
            <button
              onClick={resetRecording}
              className="text-white/60 text-xs font-semibold"
            >
              Cancel
            </button>
          </div>

          <div className="text-center py-2">
            <div className="text-[#F97316] text-xs font-bold tracking-[0.2em] uppercase mb-2">
              Now speaking to leadership
            </div>
            <h3 className="text-white text-lg font-bold leading-tight px-4">
              Speak naturally. Tell them what's on your mind.
            </h3>
          </div>

          <div className="flex justify-center py-4">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full bg-[#F97316]/20 animate-ping"
                style={{ animationDuration: "1.5s" }}
              />
              <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-2xl">
                <Mic size={52} className="text-white" strokeWidth={2} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-0.5 h-10">
            {WAVEFORM_HEIGHTS.map((h, i) => (
              <div
                key={i}
                className="w-1 rounded-full bg-white/70"
                style={{
                  height: `${h}px`,
                  animation: "voiceWave 1s ease-in-out infinite",
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>

          <div className="text-center">
            <div className="font-mono text-white text-4xl font-bold tracking-wider">
              {fmtTime(elapsed)}
            </div>
            <div className="text-white/50 text-xs mt-1">
              {remaining} seconds remaining
            </div>
            <div className="mt-3 w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-[#F97316] rounded-full transition-all"
                style={{ width: `${(elapsed / VOICE_MAX_SECONDS) * 100}%` }}
              />
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <button
              onClick={stopRecording}
              className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2"
            >
              <Check size={16} /> Done — Send My Message
            </button>
            <button
              onClick={resetRecording}
              className="w-full py-3 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2"
            >
              <Trash2 size={13} /> Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 3: Tag + Identity ──
  if (step === 3) {
    return (
      <div className={`fixed inset-0 z-50 ${navTheme} overflow-y-auto p-5`}>
        <div className="max-w-lg mx-auto space-y-4 pt-10">
          <button
            onClick={startRecording}
            className="flex items-center gap-1.5 text-[#BDD1F0] text-sm"
          >
            <ChevronLeft size={16} /> Re-record
          </button>

          <div>
            <div className="text-[9px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase mb-1">
              Final Step
            </div>
            <h3 className="text-white text-lg font-bold">Confirm and send</h3>
          </div>

          {/* Playback */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F97316] flex items-center justify-center flex-shrink-0">
              <Play size={14} className="text-white ml-0.5" fill="white" />
            </div>
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">
                Your message
              </div>
              <div className="text-white/60 text-xs">
                {fmtTime(elapsed)} · Tap to listen back
              </div>
            </div>
            <button onClick={resetRecording}>
              <Trash2 size={14} className="text-white/50" />
            </button>
          </div>

          {/* Topics */}
          <div>
            <div className="text-[#BDD1F0] text-[10px] font-bold tracking-wider uppercase mb-2">
              Topics discussed
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {[...selectedTopics].map((label) => (
                <button
                  key={label}
                  onClick={() => toggleTopic(label)}
                  className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#F97316]/20 text-[#FED7AA] border border-[#F97316]/40 flex items-center gap-1"
                >
                  <Check size={8} strokeWidth={3} />
                  {label}
                </button>
              ))}
              {voiceTopicList
                .filter((t) => !selectedTopics.has(t.label))
                .slice(0, 3)
                .map((t) => (
                  <button
                    key={t.label}
                    onClick={() => toggleTopic(t.label)}
                    className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-white/5 text-white/50 border border-white/15 border-dashed"
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
            </div>
            <p className="text-white/40 text-[10px]">
              Pre-tagged from your conversation. Edit if needed.
            </p>
          </div>

          {/* Identity */}
          <div>
            <div className="text-[#BDD1F0] text-[10px] font-bold tracking-wider uppercase mb-2">
              Want a response?
            </div>
            <div className="space-y-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                className="w-full px-3.5 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F97316]/50"
              />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional)"
                className="w-full px-3.5 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F97316]/50"
              />
            </div>
          </div>

          <div className="bg-[#F97316]/10 border border-[#F97316]/30 rounded-xl p-3 flex items-start gap-2.5">
            <Shield size={13} className="text-[#F97316] flex-shrink-0 mt-0.5" />
            <div className="text-[#BDD1F0] text-[10px] leading-relaxed">
              Goes only to LD4 leadership for follow-up within 7 days. Never
              sold or shared.
            </div>
          </div>

          {submitError && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3.5 text-red-300 text-xs leading-relaxed">
              {submitError}
            </div>
          )}

          <button
            onClick={() => submitVoice(false)}
            disabled={submitting}
            className="w-full py-3.5 rounded-xl bg-white text-[#17253F] font-bold text-sm flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
          >
            {submitting ? (
              "Sending…"
            ) : (
              <>
                <Send size={15} /> Send to Leadership
              </>
            )}
          </button>
          <button
            onClick={() => submitVoice(true)}
            disabled={submitting}
            className="w-full py-2.5 text-white/60 text-sm text-center disabled:opacity-40"
          >
            {submitting ? "Sending…" : "Send Anonymously Instead"}
          </button>
        </div>
      </div>
    );
  }

  // ── STEP 4: Confirmation ──
  return (
    <div className={`fixed inset-0 z-50 ${navTheme} overflow-y-auto p-5`}>
      <div className="max-w-lg mx-auto space-y-4 pt-10 text-center">
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-2xl">
            <Check size={40} className="text-white" strokeWidth={3} />
          </div>
        </div>

        <div className="text-[#F97316] text-xs font-bold tracking-[0.2em] uppercase">
          Message Delivered
        </div>
        <h2 className="text-white text-2xl font-bold">
          {anonymous || !name ? "Thank you." : `Thank you, ${name}.`}
        </h2>
        <p className="text-[#BDD1F0] text-sm leading-relaxed">
          Your voice is now part of the LD4 leadership digest.
          {!anonymous && name && " You'll hear back within 7 days."}
        </p>

        {selectedTopics.size > 0 && (
          <div className="flex flex-wrap gap-1.5 justify-center">
            {[...selectedTopics].map((label) => (
              <span
                key={label}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#F97316]/15 text-[#F97316] border border-[#F97316]/30"
              >
                {label}
              </span>
            ))}
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-left">
          <div className="text-[10px] font-bold text-[#BDD1F0] tracking-[0.16em] uppercase mb-3">
            What happens next
          </div>
          {[
            "Your message reaches the LD4 chair this week",
            "Reps representing PC-04-12 receive a digest",
            ...(!anonymous && name
              ? ["You'll get a personal acknowledgment within 7 days"]
              : []),
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-3 mb-2.5 last:mb-0">
              <div className="w-5 h-5 rounded-full bg-[#F97316]/20 border border-[#F97316]/40 flex items-center justify-center flex-shrink-0">
                <span className="text-[#F97316] text-[9px] font-bold">
                  {i + 1}
                </span>
              </div>
              <span className="text-white text-xs leading-relaxed">{text}</span>
            </div>
          ))}
        </div>

        <button
          onClick={onDone}
          className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-sm flex items-center justify-center gap-2"
        >
          <ArrowRight size={15} /> Back to Log Interaction
        </button>
      </div>
    </div>
  );
};

export const LogInteractionModal = ({
  onClose,
  onLog,
  user,
  facility,
  initialType,
}) => {
  const [type, setType] = useState(initialType || "door");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recSeconds, setRecSeconds] = useState(0);
  const [voiceFlow, setVoiceFlow] = useState(false);
  const [voiceFlowDone, setVoiceFlowDone] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const canRecord = !!navigator.mediaDevices?.getUserMedia;

  const fmtTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const startRecording = async () => {
    try {
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecSeconds(0);
      timerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    } catch {
      alert("Microphone permission denied.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const discardAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecSeconds(0);
  };

  useEffect(
    () => () => {
      clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    },
    [],
  );

  const [uploading, setUploading] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [surveyStep, setSurveyStep] = useState(false);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fid = facility?.facility_id;
    if (!fid) return;
    authFetch(`/fieldiq/facility-survey?facility_id=${encodeURIComponent(fid)}`)
      .then((data) => {
        if (data?.survey) setActiveSurvey(data.survey);
      })
      .catch(() => {});
  }, []);

  if (voiceFlow) {
    return (
      <LogInteractionVoiceFlow
        contactName={name}
        preTopics={[]}
        facility={facility}
        onDone={() => {
          setVoiceFlow(false);
          setVoiceFlowDone(true);
        }}
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const ts = new Date().toISOString();
    let serverId = null;

    if (audioBlob) {
      // Always keep a local backup in IndexedDB
      await saveAudioBlob(ts, audioBlob);

      // Upload to server for Whisper + GPT processing
      setUploading(true);
      try {
        const fd = new FormData();
        const ext = audioBlob.type.includes("webm") ? "webm" : "mp4";
        fd.append("audio", audioBlob, `recording.${ext}`);
        fd.append("facility_id", facility?.facility_id || "");
        fd.append("interaction_type", type);
        fd.append("person_name", name.trim());
        fd.append("notes", note.trim());
        const token = localStorage.getItem("fieldiq_token");
        const resp = await fetch(`${FIELDIQ_API_URL}/interactions/audio`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (resp.ok) {
          const data = await resp.json();
          serverId = data.id;
        }
      } catch {
        // Upload failure is non-fatal — recording still in IndexedDB
      } finally {
        setUploading(false);
      }
    }

    const entry = {
      type,
      name: name.trim(),
      // If audio was uploaded, show "Transcribing…" until the summary lands
      note: audioBlob && serverId ? "Transcribing…" : note.trim(),
      label: INTERACTION_TYPES.find((t) => t.id === type)?.label || type,
      hasAudio: !!audioBlob,
      audioKey: audioBlob ? ts : null,
      serverId,
      // Server pipeline status: processing → complete | error.
      // A failed upload goes straight to error so the feed offers re-upload.
      audioStatus: audioBlob ? (serverId ? "processing" : "error") : null,
      uploadedAt: serverId ? new Date().toISOString() : null,
      // Original typed note, kept for re-upload (note may be overwritten
      // by the transcript summary)
      rawNote: note.trim(),
    };
    logActivity(entry);
    onLog(entry);

    // Persist to database (non-blocking — failure is not user-visible)
    authFetch("/fieldiq/interactions", {
      method: "POST",
      body: JSON.stringify({
        facility_id: facility?.facility_id || null,
        interaction_type: type,
        person_name: name.trim(),
        notes: entry.note,
        has_audio: !!audioBlob,
        audio_activity_id: serverId,
      }),
    }).catch(() => {});

    if (activeSurvey) {
      setSurveyLoading(true);
      const issueToken = (lat, lng) =>
        authFetch("/fieldiq/survey-share-token", {
          method: "POST",
          body: JSON.stringify({
            survey_id: activeSurvey.id,
            facility_id: facility?.facility_id || null,
            lat,
            lng,
          }),
        })
          .then((data) => {
            if (data?.token) {
              setShareToken(data.token);
              setSurveyStep(true);
            } else {
              onClose();
            }
          })
          .catch(() => onClose())
          .finally(() => setSurveyLoading(false));

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => issueToken(pos.coords.latitude, pos.coords.longitude),
          () => issueToken(null, null),
          { timeout: 5000, maximumAge: 60000 },
        );
      } else {
        issueToken(null, null);
      }
    } else {
      onClose();
    }
  };

  if (surveyLoading) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center"
        style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      >
        <div className="bg-slate-900 border border-slate-700 rounded-t-2xl w-full max-w-lg p-8 flex flex-col items-center gap-4">
          <Loader2 size={28} className="text-cyan-400 animate-spin" />
          <p className="text-slate-300 text-sm">Preparing survey link…</p>
        </div>
      </div>
    );
  }

  if (surveyStep && activeSurvey && shareToken) {
    const surveyUrl = `https://app.hyveappliedintelligence.com/s/c/${activeSurvey.public_token}?st=${shareToken}`;
    const handleNativeShare = () => {
      if (navigator.share) {
        navigator
          .share({ title: activeSurvey.title, url: surveyUrl })
          .catch(() => {});
      } else {
        window.open(`sms:?body=${encodeURIComponent(surveyUrl)}`, "_self");
      }
    };
    const handleCopy = () => {
      navigator.clipboard?.writeText(surveyUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center"
        style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      >
        <div className="bg-slate-900 border border-slate-700 rounded-t-2xl w-full max-w-lg p-5 pb-8 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <QrCode size={16} className="text-cyan-400" />
              <h2 className="text-white font-semibold">Share Survey</h2>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-lg leading-none"
            >
              ✕
            </button>
          </div>
          <p className="text-slate-400 text-sm">{activeSurvey.title}</p>
          <div className="flex justify-center">
            <div className="bg-white rounded-xl p-4">
              <QRCodeSVG value={surveyUrl} size={176} level="M" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleNativeShare}
              className="flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <Share2 size={15} />
              Send / Share
            </button>
            <button
              onClick={handleCopy}
              className={`flex items-center justify-center gap-2 py-3 border rounded-xl text-sm font-medium transition-colors ${
                copied
                  ? "bg-cyan-500/10 border-cyan-500 text-cyan-300"
                  : "bg-slate-800 border-slate-600 text-slate-300 hover:border-cyan-500"
              }`}
            >
              {copied ? <Check size={15} /> : <ClipboardList size={15} />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
          <button
            onClick={onClose}
            className="w-full py-2.5 text-slate-500 text-sm"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-t-2xl w-full max-w-lg p-5 pb-8 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold">Log Interaction</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type picker */}
          <div className="grid grid-cols-4 gap-2">
            {INTERACTION_TYPES.map(({ id, label, icon: Icon, color }) => (
              <button
                key={id}
                type="button"
                onClick={() => setType(id)}
                className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-xs transition-colors ${
                  type === id
                    ? "border-cyan-500 bg-cyan-500/10 text-white"
                    : "border-slate-700 bg-slate-800 text-slate-400"
                }`}
              >
                <Icon
                  size={18}
                  className={type === id ? "text-cyan-400" : color}
                />
                {label}
              </button>
            ))}
          </div>

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Person's name (optional)"
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notes (optional)"
            rows={2}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none resize-none"
          />

          {/* Voice Notes */}
          {canRecord && (
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-xs font-semibold text-slate-300">
                  Voice Notes
                </p>
                <p className="text-[11px] text-slate-500">
                  Capture what you just learned
                </p>
              </div>
              {!audioBlob ? (
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    recording
                      ? "bg-red-600 text-white animate-pulse"
                      : "bg-slate-800 border border-slate-600 text-slate-300 hover:border-red-500 hover:text-red-400"
                  }`}
                >
                  {recording ? (
                    <>
                      <Square size={15} className="fill-white" />
                      Stop · {fmtTime(recSeconds)}
                    </>
                  ) : (
                    <>
                      <Mic size={15} />
                      Voice Notes
                    </>
                  )}
                </button>
              ) : (
                <div className="w-full space-y-1.5">
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Mic size={12} className="text-green-400" />
                    Recording saved · {fmtTime(recSeconds)}
                  </p>
                  <audio
                    src={audioUrl}
                    controls
                    className="w-full h-9 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={discardAudio}
                    className="text-xs text-slate-500 hover:text-red-400 underline"
                  >
                    Discard recording
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Your Voice button */}
          {voiceFlowDone ? (
            <div className="w-full py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3 px-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <Check
                  size={13}
                  className="text-emerald-300"
                  strokeWidth={2.5}
                />
              </div>
              <div>
                <div className="text-white text-xs font-semibold">
                  Your Voice captured
                </div>
                <div className="text-emerald-400 text-[10px]">
                  Sent to LD4 leadership
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setVoiceFlow(true)}
              className="w-full py-2.5 rounded-xl bg-gradient-to-br from-[#F97316]/15 to-[#EA580C]/5 border border-[#F97316]/40 flex items-center gap-3 px-3 text-left"
            >
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center flex-shrink-0">
                <Megaphone size={13} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-semibold flex items-center gap-1.5">
                  Your Voice
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-[#F97316]/30 text-[#FED7AA]">
                    NEW
                  </span>
                </div>
                <div className="text-[#FED7AA] text-[10px]">
                  Send voter's message to leadership
                </div>
              </div>
              <ChevronRight
                size={13}
                className="text-[#F97316] flex-shrink-0"
              />
            </button>
          )}

          <button
            type="submit"
            disabled={uploading}
            className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-medium rounded-xl text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {uploading && <Loader2 size={15} className="animate-spin" />}
            {uploading ? "Uploading…" : "Save Interaction"}
          </button>
        </form>
      </div>
    </div>
  );
};
